import { useState, type ReactNode } from 'react'
import { trackCta } from '../../lib/analytics'
import { Link } from 'react-router-dom'
import { MarketingShell } from '../../components/MarketingShell'
import { Seo, SITE_URL } from '../../components/Seo'
import { WaitlistForm } from '../../components/marketing/WaitlistForm'
import { Modal } from '../../components/ui/Modal'
import { useScrollDepth } from '../../lib/useScrollDepth'
import { Faq, faqJsonLd } from '../../components/Faq'
import { SHARED_FAQ } from '../../lib/marketing-faq'
import { PLAN_OPTIONS, WAITLIST_MODE } from '../../lib/plans'
import { PROVIDER_LOGOS } from '../../components/icons/ProviderLogos'
import '../../components/marketing-chrome.css'
import './home.css'

// Exported so entry-server.tsx (build-time prerender) and this file share
// one source -- see that file's own comment.
export const seoTitle = 'RemindLoop — Invoice Reminders on WhatsApp & SMS for South African Businesses'
export const seoDescription =
  'RemindLoop follows up on your unpaid invoices on WhatsApp, with SMS as backup, so you don\'t have to — connected to Xero and QuickBooks (Sage coming soon), checked against your ledger before every send, and logged end to end. Join the waitlist for early access.'
export const seoJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organisation',
    name: 'RemindLoop',
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    description:
      'RemindLoop is a B2B pre-collection and reminder service that sends automated WhatsApp and SMS payment reminders for invoices, appointments, and renewals imported from Xero, QuickBooks, or CSV (Sage coming soon).',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'RemindLoop',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'Automated WhatsApp and SMS invoice payment reminders with Xero and QuickBooks integrations (Sage coming soon), a custom reminder schedule, and opt-out compliance built in.',
  },
  faqJsonLd(SHARED_FAQ),
]

/**
 * "Get started"/"Start free trial" CTA for this page -- a real <a> (via
 * react-router Link) to /register normally, or a button opening the
 * waitlist modal while WAITLIST_MODE is on (see lib/plans.ts and
 * WaitlistForm.tsx's WaitlistCta, which this mirrors). Not reusing
 * WaitlistCta/ButtonLink/Button directly: those render through shadcn's
 * buttonVariants(), whose own utility classes (padding, radius, color)
 * would fight this page's bespoke .rl-btn-cta/.rl-btn-ghost rather than
 * cleanly override it the way a plain Tailwind className swap does on
 * the rest of the site's lighter CTA overrides.
 */
function HomeCta({
  to,
  source,
  planInterest,
  variant = 'cta',
  children,
}: {
  to: string
  source: string
  planInterest?: string
  variant?: 'cta' | 'ghost'
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const className = variant === 'cta' ? 'rl-btn-cta' : 'rl-btn-ghost'

  if (!WAITLIST_MODE) {
    return (
      <Link to={to} className={className} onClick={() => trackCta(source, planInterest)}>
        {children}
      </Link>
    )
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => {
          trackCta(source, planInterest)
          setOpen(true)
        }}
      >
        {children}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Join the waitlist">
        <WaitlistForm source={source} planInterest={planInterest} />
      </Modal>
    </>
  )
}

/** Quiet "Join the waitlist →" text link that opens the same dialog. */
function WaitlistTextLink({ source }: { source: string }) {
  const [open, setOpen] = useState(false)
  if (!WAITLIST_MODE) {
    return (
      <Link to="/register" className="rl-textlink" onClick={() => trackCta(source)}>
        Join the waitlist <span aria-hidden="true">→</span>
      </Link>
    )
  }
  return (
    <>
      <button type="button" className="rl-textlink" onClick={() => setOpen(true)}>
        Join the waitlist <span aria-hidden="true">→</span>
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Join the waitlist">
        <WaitlistForm source={source} />
      </Modal>
    </>
  )
}

function CheckIcon({ color = 'var(--mkt-cyan-accent)' }: { color?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: 'none', marginTop: 5 }}
    >
      <path d="M3 8.5l3.2 3L13 4.5" />
    </svg>
  )
}

const RULE_STEPS = [
  { dot: 'rl-d1 rl-o1', title: '3 days before due', sub: 'WhatsApp · friendly nudge' },
  { dot: 'rl-d2 rl-o2', title: 'On the due date', sub: 'WhatsApp · amount and reference' },
  { dot: 'rl-d3 rl-o3', title: '5 days overdue', sub: 'WhatsApp, with SMS as fallback' },
]

const INTEGRATIONS: { name: string; logo?: keyof typeof PROVIDER_LOGOS; note?: string }[] = [
  { name: 'Xero', logo: 'xero' },
  { name: 'QuickBooks', logo: 'quickbooks' },
  { name: 'Sage', logo: 'sage', note: 'coming soon' },
  { name: 'CSV import' },
]

const HOW_STEPS = [
  {
    num: '01',
    title: 'Connect your ledger',
    body: 'Link Xero or QuickBooks, or import a CSV. Your open invoices come across on their own.',
  },
  {
    num: '02',
    title: 'Set your reminder schedule',
    body: 'Choose when reminders go out and on which channel. Start from a built-in template and adjust.',
  },
  {
    num: '03',
    title: 'We do the chasing',
    body: 'WhatsApp first, SMS as backup, on your schedule. Before each message goes out we check your ledger, so a paid invoice is never chased.',
  },
]

// Starter/Business/Scale feature bullets per the approved reference --
// a condensed, homepage-specific list, deliberately not Pricing.tsx's
// fuller planFeatures object (which includes FAQ-adjacent detail this
// page has no room for). Price/included-accounts/overage rate below are
// read from PLAN_OPTIONS (lib/plans.ts) instead of being repeated here,
// so this page and /pricing can never quote different numbers.
const PLAN_FEATURES: Record<string, string[]> = {
  starter: [
    'WhatsApp and SMS reminders',
    'Delivery, read and response tracking',
    'Exportable message record per customer',
    '1 accounting connection',
  ],
  business: [
    'Everything in Starter',
    `${PLAN_OPTIONS[1].includedMessages / PLAN_OPTIONS[0].includedMessages} times the messages of Starter`,
    `R${PLAN_OPTIONS[1].overageRate} per extra customer account, not R${PLAN_OPTIONS[0].overageRate}`,
    '2 accounting connections',
  ],
  scale: [
    'Everything in Business',
    `R${PLAN_OPTIONS[2].overageRate} per extra customer account, our lowest rate`,
    'Room for up to 2,000 customer accounts',
    '5 accounting connections',
  ],
}

/** List price struck through, early-access price beside it. */
function PlanPrice({ price, early }: { price: number; early: number }) {
  return (
    <div>
      <div className="rl-amt">
        <span className="rl-amt-n">R{early.toLocaleString('en-US')}</span>
        <span className="rl-amt-p">/ month</span>
        <del className="rl-amt-old">
          <span className="rl-sr">List price </span>R{price.toLocaleString('en-US')}
        </del>
      </div>
      <div className="rl-amt-note">Early-access price for 12 months</div>
    </div>
  )
}

export default function Home() {
  useScrollDepth()
  const starter = PLAN_OPTIONS.find((p) => p.value === 'starter')!
  const business = PLAN_OPTIONS.find((p) => p.value === 'business')!
  const scale = PLAN_OPTIONS.find((p) => p.value === 'scale')!

  return (
    <MarketingShell>
      <Seo title={seoTitle} description={seoDescription} path="/" jsonLd={seoJsonLd} />

      <div className="rl-home rl-dark">
        {/* Hero: split layout (copy left, example message right on wide
            screens; stacked with the message under the buttons on phones) */}
        <section className="rl-hero-sec" style={{ position: 'relative' }}>
          <div className="rl-grid-bg" aria-hidden="true" />
          <div className="rl-glow rl-glow-a" aria-hidden="true" />
          <div className="rl-glow rl-glow-b" aria-hidden="true" />
          <div className="rl-wrap rl-hero-grid" style={{ position: 'relative' }}>
            <div className="rl-hero-copy">
              <div className="rl-kicker rl-hero-in" style={{ marginBottom: 16 }}>
                Invoice reminders for South African businesses · prices in rand
              </div>
              <h1
                className="rl-hero-in"
                style={{
                  margin: 0,
                  fontSize: 'clamp(36px, 5.4vw, 60px)',
                  lineHeight: 1.06,
                  letterSpacing: '-0.025em',
                  fontWeight: 600,
                  textWrap: 'balance',
                  color: 'var(--mkt-fg)',
                }}
              >
                Get paid on time.{' '}
                <em style={{ fontStyle: 'italic', fontWeight: 400, color: 'var(--mkt-cyan-accent)' }}>
                  We chase your unpaid invoices on WhatsApp.
                </em>
              </h1>
              <p
                className="rl-hero-in"
                style={{ margin: '20px 0 0', maxWidth: 560, fontSize: 19, lineHeight: 1.5, color: 'var(--mkt-fg-muted)' }}
              >
                Your customers read WhatsApp. We follow up, so you don&rsquo;t have to.
              </p>
              <p
                className="rl-hero-in rl-hero-extra"
                style={{ margin: '12px 0 0', maxWidth: 560, fontSize: 15.5, lineHeight: 1.5, color: 'var(--mkt-fg-muted-2)' }}
              >
                SMS is the backup when WhatsApp can&rsquo;t reach a customer. Reminders keep going on
                your schedule until the invoice is paid.
              </p>
              <div className="rl-hero-in rl-hero-cta">
                <HomeCta to="/register" source="home">
                  Join the waitlist
                </HomeCta>
                <a className="rl-btn-ghost" href="#how">
                  See how it works
                </a>
              </div>
            </div>
            <div className="rl-hero-card rl-hero-in" aria-label="Example reminder">
              <div className="rl-hero-card-head">
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="rl-live" aria-hidden="true" />
                  Invoice 1042 · Ama · R4,800
                </span>
                <span className="rl-tag-ex">Example</span>
              </div>
              <div className="rl-bub" style={{ marginTop: 16 }}>
                Hi Ama — invoice 1042 for R4,800 is due this Friday. You can pay by EFT any time
                before then. Thank you!
                <span className="rl-bub-m">09:00</span>
              </div>
              <span className="rl-sysline" style={{ marginTop: 14 }}>
                <CheckIcon color="#FF90B1" />
                Paid in Xero · reminders stopped
              </span>
              <p className="rl-hero-note">
                Example: when the invoice is paid in Xero or QuickBooks, reminders stop.
              </p>
            </div>
          </div>
          {/* Proof slot: the early-access offer until a real customer quote
              or result exists. Do not add an invented quote here. */}
          <div className="rl-wrap" style={{ position: 'relative' }}>
            <div className="rl-proof">
              <div>
                <div className="rl-proof-t">Early access: 40% off for 12 months</div>
                <div className="rl-proof-s">
                  Join the waitlist to claim it. The offer is for our first 25 customers.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations strip */}
        <section style={{ padding: '104px 0 0' }}>
          <div className="rl-wrap rl-reveal" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13.5, color: 'var(--mkt-fg-muted-2)', letterSpacing: '0.02em', marginBottom: 22 }}>
              Pulls your open invoices straight from the tools you already use
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'baseline',
                gap: '14px 48px',
                fontSize: 26,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'color-mix(in srgb, var(--mkt-fg) 80%, transparent)',
              }}
            >
              {INTEGRATIONS.map((item) => {
                const Logo = item.logo ? PROVIDER_LOGOS[item.logo] : null
                return (
                <span key={item.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  {Logo && <Logo width={30} height={30} aria-hidden="true" />}
                  {item.name}
                  {item.note && (
                    <span style={{ fontSize: 13, fontWeight: 400, letterSpacing: '0.02em', color: 'var(--mkt-fg-muted-2)' }}>
                      {' '}
                      {item.note}
                    </span>
                  )}
                </span>
                )
              })}
            </div>
            <p
              style={{
                margin: '22px auto 0',
                maxWidth: 560,
                textAlign: 'center',
                fontSize: 14,
                lineHeight: 1.5,
                color: 'var(--mkt-fg-muted-2)',
              }}
            >
              CSV imports have no ledger to check, so reminders continue until you mark the invoice paid.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section id="how" style={{ padding: '150px 0 0' }}>
          <div className="rl-wrap">
            <div className="rl-reveal" style={{ maxWidth: 640, marginBottom: 56 }}>
              <div className="rl-kicker">How it works</div>
              <h2 className="rl-h2">Connect once. We do the chasing.</h2>
              <p className="rl-lede">Three steps, and the Friday-afternoon follow-ups are no longer yours.</p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
              {HOW_STEPS.map((step) => (
                <article key={step.num} className="rl-card rl-reveal" style={{ flex: '1 1 300px' }}>
                  <div className="rl-num">{step.num}</div>
                  <h3 className="rl-card-t">{step.title}</h3>
                  <p className="rl-card-p">{step.body}</p>
                </article>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 56 }}>
            {/* Product panel */}
            <div
              className="rl-in"
              style={{
                                width: '100%',
                maxWidth: 1000,
                marginTop: 0,
                textAlign: 'left',
                border: '1px solid var(--mkt-hairline-strong)',
                borderRadius: 4,
                background: 'linear-gradient(180deg, color-mix(in srgb, #ffffff 7%, transparent), color-mix(in srgb, #ffffff 2%, transparent))',
                boxShadow: '0 40px 120px color-mix(in srgb, var(--mkt-cyan) 10%, transparent), 0 12px 32px rgba(0,0,0,.45)',
                overflow: 'hidden',
              }}
            >
              <div
                className="rl-panel-head"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  padding: '14px 20px',
                  borderBottom: '1px solid var(--mkt-hairline)',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="rl-live" aria-hidden="true" />
                  Invoice 1042 · Ama · R4,800
                </span>
                <span className="rl-tag-ex">Example</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                <div className="rl-seqcol" style={{ flex: '1 1 360px', padding: 28, borderRight: '1px solid var(--mkt-hairline)' }}>
                  <div className="rl-kicker" style={{ marginBottom: 22 }}>
                    Your rules
                  </div>
                  <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {RULE_STEPS.map((step) => (
                      <li key={step.title} className="rl-seq">
                        <span className={`rl-dot ${step.dot}`} aria-hidden="true" />
                        <div>
                          <div className="rl-seq-t">{step.title}</div>
                          <div className="rl-seq-s">{step.sub}</div>
                        </div>
                      </li>
                    ))}
                    <li className="rl-seq rl-dim" style={{ paddingBottom: 0 }}>
                      <span className="rl-dot rl-d4" aria-hidden="true" />
                      <div>
                        <div className="rl-seq-t">Still unpaid</div>
                        <div className="rl-seq-s">Handed back to you</div>
                      </div>
                    </li>
                  </ol>
                </div>
                <div
                  style={{
                    flex: '1 1 380px',
                    padding: 28,
                    background: 'rgba(0,0,0,.18)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      paddingBottom: 14,
                      borderBottom: '1px solid var(--mkt-hairline)',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: 'color-mix(in srgb, var(--mkt-cyan) 16%, transparent)',
                        border: '1px solid color-mix(in srgb, var(--mkt-cyan) 40%, transparent)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--mkt-cyan-light)',
                      }}
                    >
                      A
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>Ama</span>
                    <span style={{ fontSize: 13, color: 'var(--mkt-fg-muted-2)' }}>WhatsApp</span>
                  </div>
                  <div className="rl-bub rl-m1">
                    Hi Ama — invoice 1042 for R4,800 is due this Friday. You can pay by EFT any
                    time before then. Thank you!
                    <span className="rl-bub-m">09:00</span>
                  </div>
                  <div className="rl-bub rl-m2">
                    Hi Ama — invoice 1042 (R4,800) is due today. Reference: INV-1042.
                    <span className="rl-bub-m">09:00</span>
                  </div>
                  <div className="rl-bub rl-m3">
                    Hi Ama — invoice 1042 is now 5 days overdue. Could you let us know when to
                    expect payment?
                    <span className="rl-bub-m">09:00</span>
                  </div>
                  <span className="rl-sysline rl-m4">
                    <CheckIcon color="#FF90B1" />
                    Paid in Xero · reminders stopped
                  </span>
                </div>
              </div>
            </div>
            </div>
            <div className="rl-textlink-row">
              <WaitlistTextLink source="home-how" />
            </div>
          </div>
        </section>

        {/* Why it works */}
        <section id="why" style={{ padding: '150px 0 0' }}>
          <div className="rl-wrap">
            <div className="rl-reveal" style={{ maxWidth: 680, marginBottom: 56 }}>
              <div className="rl-kicker">Why it works</div>
              <h2 className="rl-h2">Reaches people where they read. Checks before every send.</h2>
              <p className="rl-lede">
                The reminders are polite, personal and early. We start before the due date.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
              <article className="rl-card rl-reveal" style={{ flex: '1 1 300px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 30, height: 64 }}>
                  <span className="rl-pill rl-pill-cyan">WhatsApp</span>
                  <svg width="20" height="12" viewBox="0 0 20 12" fill="none" stroke="var(--mkt-fg-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M1 6h17M13 1l5 5-5 5" />
                  </svg>
                  <span className="rl-pill">SMS fallback</span>
                </div>
                <h3 className="rl-card-t">Reaches people where they read</h3>
                <p className="rl-card-p">
                  WhatsApp first, because that is where your customers already are. If WhatsApp
                  cannot reach someone, an SMS follows automatically.
                </p>
              </article>
              <article className="rl-card rl-reveal" style={{ flex: '1 1 300px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 30, height: 64 }}>
                  <span className="rl-mdot" style={{ background: 'var(--mkt-cyan-light)' }} aria-hidden="true" />
                  <span className="rl-mline" aria-hidden="true" />
                  <span className="rl-mdot" aria-hidden="true" />
                  <span className="rl-mline" aria-hidden="true" />
                  <span className="rl-mdot" style={{ background: 'var(--mkt-cyan)' }} aria-hidden="true" />
                </div>
                <h3 className="rl-card-t">Follows up so you don&rsquo;t have to</h3>
                <p className="rl-card-p">
                  A reminder schedule you set, starting before the due date. Each message names the customer
                  and the invoice, and reads like a note from you, not a collections letter.
                </p>
              </article>
              <article className="rl-card rl-reveal" style={{ flex: '1 1 300px' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 6,
                    marginBottom: 30,
                    height: 64,
                    fontSize: 13.5,
                    lineHeight: 1.3,
                    color: 'var(--mkt-fg-muted-2)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <div>09:00 · Sent to Ama on WhatsApp</div>
                  <div>09:01 · Delivered</div>
                  <div style={{ color: 'var(--mkt-magenta-paid)' }}>14:32 · Paid, reminders stopped</div>
                </div>
                <h3 className="rl-card-t">Checks first, and keeps the record</h3>
                <p className="rl-card-p">
                  Just before each reminder goes out we check your ledger. If the invoice has been
                  paid, nothing is sent and it is marked paid. Every message is logged, start to
                  finish.
                </p>
              </article>
            </div>
            <div className="rl-textlink-row">
              <WaitlistTextLink source="home-why" />
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" style={{ padding: '150px 0 0' }}>
          <div className="rl-wrap">
            <div className="rl-reveal" style={{ maxWidth: 680, marginBottom: 56 }}>
              <div className="rl-kicker">Pricing</div>
              <h2 className="rl-h2">Priced by the customers you chase.</h2>
              <p className="rl-lede">All plans include WhatsApp and SMS reminders. Plans differ by volume and accounting connections. A customer with several overdue invoices counts once. Early adopters get 40% off for 12 months.</p>
            </div>
            {/* 'en-US' deliberately, not 'en-ZA': ICU's en-ZA grouping
                separator is a space (" 1 199"), not a comma -- this
                page's "R1,199" convention (matching Pricing.tsx's own
                hardcoded planPriceDisplay) needs a comma, which 'en-US'
                guarantees regardless of host locale data. Caught via a
                failing Playwright assertion, not assumed. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'stretch' }}>
              <article className="rl-card rl-price rl-reveal" style={{ flex: '1 1 280px' }}>
                <h3 className="rl-plan">Starter</h3>
                <PlanPrice price={starter.price} early={starter.earlyAdopterPrice} />
                <p className="rl-inc">
                  {starter.includedAccounts} active customer accounts and {starter.includedMessages} messages a month. R{starter.overageRate} per extra account.
                </p>
                <ul className="rl-feat">
                  {PLAN_FEATURES.starter.map((f) => (
                    <li key={f}>
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
                <HomeCta to="/register?plan=starter" source="home-pricing" planInterest="starter" variant="ghost">
                  Join the waitlist
                </HomeCta>
              </article>
              <article className="rl-card rl-price rl-price-hi rl-reveal" style={{ flex: '1 1 280px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <h3 className="rl-plan">Business</h3>
                  <span
                    style={{
                      fontSize: 12,
                      letterSpacing: '0.02em',
                      padding: '3px 8px',
                      borderRadius: 2,
                      background: 'color-mix(in srgb, var(--mkt-cyan) 14%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--mkt-cyan) 45%, transparent)',
                      color: 'var(--mkt-cyan-light)',
                    }}
                  >
                    Most popular
                  </span>
                </div>
                <PlanPrice price={business.price} early={business.earlyAdopterPrice} />
                <p className="rl-inc">
                  {business.includedAccounts} active customer accounts and {business.includedMessages} messages a month. R{business.overageRate} per extra account.
                </p>
                <ul className="rl-feat">
                  {PLAN_FEATURES.business.map((f) => (
                    <li key={f}>
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
                <HomeCta to="/register?plan=business" source="home-pricing" planInterest="business">
                  Join the waitlist
                </HomeCta>
              </article>
              <article className="rl-card rl-price rl-reveal" style={{ flex: '1 1 280px' }}>
                <h3 className="rl-plan">Scale</h3>
                <PlanPrice price={scale.price} early={scale.earlyAdopterPrice} />
                <p className="rl-inc">
                  {scale.includedAccounts} active customer accounts and {scale.includedMessages.toLocaleString('en-US')} messages a
                  month. R{scale.overageRate} per extra account, up to 2,000.
                </p>
                <ul className="rl-feat">
                  {PLAN_FEATURES.scale.map((f) => (
                    <li key={f}>
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
                <HomeCta to="/register?plan=scale" source="home-pricing" planInterest="scale" variant="ghost">
                  Join the waitlist
                </HomeCta>
              </article>
            </div>
            <div
              className="rl-card rl-reveal"
              style={{
                marginTop: 20,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 20,
                padding: '24px 28px',
              }}
            >
              <div style={{ flex: '1 1 380px' }}>
                <h3 className="rl-plan" style={{ marginBottom: 6 }}>
                  Enterprise
                </h3>
                <p className="rl-card-p" style={{ fontSize: 15 }}>
                  More than 2,000 active customer accounts? Custom pricing, and a dedicated
                  WhatsApp number instead of the shared pool.
                </p>
              </div>
              <Link to="/contact?reason=enterprise" className="rl-btn-ghost">
                Contact us
              </Link>
            </div>
            <p style={{ margin: '22px 0 0', fontSize: 15, lineHeight: 1.5, color: 'var(--mkt-fg-muted)', maxWidth: 720 }}>
              Early-access price. Join the waitlist to claim it. The offer is for our first 25
              customers.
            </p>
            <p style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.5, color: 'var(--mkt-fg-faint)', maxWidth: 720 }}>
              One message is one WhatsApp or SMS we send to a customer. An active customer account is one with at least one invoice in an open reminder
              schedule during the billing period.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" style={{ padding: '150px 0 0' }}>
          <div className="rl-wrap">
            <div className="rl-reveal" style={{ maxWidth: 680, marginBottom: 40 }}>
              <div className="rl-kicker">FAQ</div>
              <h2 className="rl-h2">Questions owners ask first.</h2>
            </div>
            <div className="rl-faq" style={{ maxWidth: 800 }}>
              <Faq items={SHARED_FAQ} title="" />
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section style={{ padding: '150px 0 0' }}>
          <div className="rl-wrap">
            <div
              className="rl-reveal"
              style={{
                position: 'relative',
                overflow: 'hidden',
                textAlign: 'center',
                padding: '84px 24px',
                border: '1px solid var(--mkt-hairline-strong)',
                borderRadius: 4,
                background: 'linear-gradient(180deg, color-mix(in srgb, #ffffff 6%, transparent), color-mix(in srgb, #ffffff 1.5%, transparent))',
              }}
            >
              <div className="rl-glow rl-glow-c" aria-hidden="true" />
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <h2 className="rl-h2" style={{ maxWidth: 640 }}>
                  Get paid without the Friday phone calls.
                </h2>
                <p className="rl-lede" style={{ maxWidth: 520 }}>
                  Connect Xero or QuickBooks, or import a CSV, and send your first real reminders.
                </p>
                <div style={{ marginTop: 36 }}>
                  <HomeCta to="/register" source="home-closing">
                    Join the waitlist
                  </HomeCta>
                </div>
                <p style={{ margin: '16px 0 0', fontSize: 13.5, color: 'var(--mkt-fg-muted-2)' }}>
                  Early access: 40% off for 12 months
                </p>
              </div>
            </div>
          </div>
        </section>
        <div style={{ height: 'clamp(60px, 8vw, 100px)' }} aria-hidden="true" />
      </div>
    </MarketingShell>
  )
}
